/**
 * RBAC Matrix - Defines permissions for each role across all modules
 * Structure: { module: { action: [roles...] } }
 */

export type RoleType = "admin" | "isso" | "ciso" | "system_owner" | "grc_manager" | "assessor" | "lead_assessor" | "auditor";

export const MODULE_ACTIONS = {
  catalog: ["create", "view", "edit", "delete", "approve", "publish", "export"],
  profile: ["create", "view", "edit", "delete", "approve", "publish", "export"],
  control_mapping: ["create", "view", "edit", "delete", "update"],
  component: ["create", "view", "edit", "delete", "export"],
  ssp: ["create", "view", "edit", "delete", "approve", "update", "export"],
  assessment_plan: ["create", "view", "edit", "delete", "approve"],
  assessment_execution: ["create", "view", "edit", "delete", "execute"],
  assessment_review: ["create", "view", "edit", "delete"],
  assessment_result: ["create", "view", "edit", "delete", "approve", "update", "export"],
  poam: ["create", "view", "edit", "delete", "update"],
  document: ["create", "view", "edit", "delete", "approve", "publish", "update", "export"],
  evidence: ["create", "view", "edit", "delete", "update", "export"],
  user: ["create", "view", "edit", "delete", "update"],
  role: ["create", "view", "edit", "delete"],
  role_mapping: ["create", "view", "edit", "delete"],
  organization: ["create", "view", "edit", "delete", "update"],
  cmdb: ["create", "view", "edit", "delete", "update"],
  validation_rule: ["create", "view", "edit", "delete", "update"],
  llm_configuration: ["create", "view", "edit", "delete", "update"],
  it_compliance: ["view", "manage", "export"],
  ciso_insights: ["view", "approve", "reject", "export"],
  scheduler: ["create", "view", "edit", "delete", "update"],
  tool: ["create", "view", "edit", "delete", "update"],
  severityColor: ["create", "view", "edit", "delete", "update"],
  cgar: ["create", "view", "update", "delete"],
} as const;

export type ModuleType = keyof typeof MODULE_ACTIONS;

/**
 * RBAC Permission Matrix
 * Maps roles to their allowed actions per module
 */
export const RBAC_MATRIX: Record<RoleType, Record<ModuleType, string[]>> = {
  admin: {
    catalog: ["create", "view", "edit", "delete"],
    profile: ["create", "view", "edit", "delete"],
    control_mapping: ["create", "view", "edit", "delete"],
    component: ["create", "view", "edit", "delete"],
    ssp: ["create", "view", "edit", "delete", "approve"],
    assessment_plan: ["create", "view", "edit", "delete"],
    assessment_execution: ["create", "view", "edit", "delete"],
    assessment_review: ["create", "view", "edit", "delete"],
    assessment_result: ["create", "view", "edit", "delete"],
    poam: ["create", "view", "edit", "delete"],
    document: ["create", "view", "edit", "delete", "update", "export"],
    evidence: ["create", "view", "edit", "delete", "update", "export"],
    user: ["create", "view", "edit", "delete"],
    role: ["create", "view", "edit", "delete"],
    role_mapping: ["create", "view", "edit", "delete"],
    organization: ["create", "view", "edit", "delete"],
    cmdb: ["create", "view", "edit", "delete"],
    validation_rule: ["create", "view", "edit", "delete"],
    llm_configuration: ["create", "view", "edit", "delete"],
    it_compliance: ["view", "manage", "export"],
    ciso_insights: ["view", "approve", "reject", "export"],
    scheduler: ["create", "view", "edit", "delete", "update"],
    tool: ["create", "view", "edit", "delete", "update"],
    severityColor: ["create", "view", "edit", "delete", "update"],
    cgar: ["create", "view", "update", "delete"],
  },
  isso: {
    catalog: ["view"],
    profile: ["view"],
    control_mapping: ["view"],
    component: ["create", "view", "edit"],
    ssp: ["create", "view", "edit"],
    assessment_plan: ["view"],
    assessment_execution: ["view"],
    assessment_review: ["view"],
    assessment_result: ["view"],
    poam: ["edit"],
    document: ["create"],
    evidence: [],
    user: ["view"],
    role: ["view"],
    role_mapping: ["view"],
    organization: ["view"],
    cmdb: ["view"],
    validation_rule: ["view"],
    llm_configuration: ["view"],
    it_compliance: ["view"],
    ciso_insights: ["view"],
    scheduler: ["view"],
    tool: ["view"],
    severityColor: ["view"],
    cgar: ["view"],
  },
  ciso: {
    catalog: ["view"],
    profile: ["view"],
    control_mapping: ["view"],
    component: ["view"],
    ssp: ["view"],
    assessment_plan: ["view"],
    assessment_execution: ["view"],
    assessment_review: ["view"],
    assessment_result: ["view"],
    poam: ["view"],
    document: [],
    evidence: [],
    user: ["view"],
    role: ["view"],
    role_mapping: ["view"],
    organization: ["view"],
    cmdb: ["view"],
    validation_rule: ["view"],
    llm_configuration: ["view"],
    it_compliance: ["view", "export"],
    ciso_insights: ["view", "approve", "reject", "export"],
    scheduler: ["view"],
    tool: ["view"],
    severityColor: ["view"],
    cgar: ["view"],
  },
  system_owner: {
    catalog: ["view"],
    profile: ["view"],
    control_mapping: ["view"],
    component: ["view"],
    ssp: ["view"],
    assessment_plan: ["view"],
    assessment_execution: ["view"],
    assessment_review: ["view"],
    assessment_result: ["view"],
    poam: ["edit"],
    document: ["create"],
    evidence: [],
    user: ["view"],
    role: ["view"],
    role_mapping: ["view"],
    organization: ["view"],
    cmdb: ["view"],
    validation_rule: ["view"],
    llm_configuration: ["view"],
    it_compliance: ["view"],
    ciso_insights: ["view"],
    scheduler: ["view"],
    tool: ["view"],
    severityColor: ["view"],
    cgar: ["create", "view", "update"],
  },
  grc_manager: {
    catalog: [],
    profile: [],
    control_mapping: [],
    component: [],
    ssp: [],
    assessment_plan: ["create", "view", "edit"],
    assessment_execution: ["view"],
    assessment_review: ["view"],
    assessment_result: ["view"],
    poam: ["view"],
    document: [],
    evidence: [],
    user: ["view"],
    role: ["view"],
    role_mapping: ["view"],
    organization: ["view"],
    cmdb: [],
    validation_rule: [],
    llm_configuration: [],
    it_compliance: ["view"],
    ciso_insights: ["view"],
    scheduler: ["view"],
    tool: ["view"],
    severityColor: ["view"],
    cgar: ["view"],
  },
  assessor: {
    catalog: ["view"],
    profile: ["view"],
    control_mapping: ["view"],
    component: ["view"],
    ssp: ["view"],
    assessment_plan: ["create", "view", "edit"],
    assessment_execution: ["create", "view", "edit"],
    assessment_review: ["edit"],
    assessment_result: ["edit"],
    poam: ["view"],
    document: ["view"],
    evidence: [],
    user: ["view"],
    role: ["view"],
    role_mapping: ["view"],
    organization: ["view"],
    cmdb: [],
    validation_rule: [],
    llm_configuration: [],
    it_compliance: [],
    ciso_insights: [],
    scheduler: ["view"],
    tool: ["view"],
    severityColor: ["view"],
    cgar: ["view"],
  },
  lead_assessor: {
    catalog: ["view"],
    profile: ["view"],
    control_mapping: ["view"],
    component: ["view"],
    ssp: ["view", "approve"],
    assessment_plan: ["view"],
    assessment_execution: ["view"],
    assessment_review: ["view"],
    assessment_result: ["view"],
    poam: ["view"],
    document: ["view"],
    evidence: [],
    user: ["view"],
    role: ["view"],
    role_mapping: ["view"],
    organization: ["view"],
    cmdb: [],
    validation_rule: [],
    llm_configuration: [],
    it_compliance: [],
    ciso_insights: [],
    scheduler: ["view"],
    tool: ["view"],
    severityColor: ["view"],
    cgar: ["view"],
  },
  auditor: {
    catalog: ["view"],
    profile: ["view"],
    control_mapping: ["view"],
    component: ["view"],
    ssp: ["view"],
    assessment_plan: ["view"],
    assessment_execution: ["view"],
    assessment_review: ["view"],
    assessment_result: ["view"],
    poam: ["view"],
    document: ["view"],
    evidence: [],
    user: ["view"],
    role: ["view"],
    role_mapping: ["view"],
    organization: ["view"],
    cmdb: ["view"],
    validation_rule: ["view"],
    llm_configuration: ["view"],
    it_compliance: ["view"],
    ciso_insights: ["view"],
    scheduler: ["view"],
    tool: ["view"],
    severityColor: ["view"],
    cgar: ["view"],
  },
};

/**
 * Get all roles that can perform a specific action on a module
 */
export const getRolesForAction = (module: ModuleType, action: string): RoleType[] => {
  return (Object.keys(RBAC_MATRIX) as RoleType[]).filter(
    (role) => RBAC_MATRIX[role][module]?.includes(action)
  );
};

/**
 * Check if a role can perform an action on a module
 */
export const canRolePerformAction = (
  role: RoleType | string,
  module: ModuleType,
  action: string
): boolean => {
  const normalizedRole = (role?.toLowerCase() ?? "") as RoleType;
  return RBAC_MATRIX[normalizedRole]?.[module]?.includes(action) ?? false;
};

/**
 * Get all permissions for a specific role
 */
export const getRolePermissions = (role: RoleType | string) => {
  const normalizedRole = (role?.toLowerCase() ?? "") as RoleType;
  return RBAC_MATRIX[normalizedRole] ?? {};
};

/**
 * Role display names
 */
export const ROLE_DISPLAY_NAMES: Record<RoleType, string> = {
  admin: "Administrator",
  isso: "ISSO",
  ciso: "CISO",
  system_owner: "System Owner",
  grc_manager: "GRC Manager",
  assessor: "Assessor",
  lead_assessor: "Lead Assessor",
  auditor: "Auditor",
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: string): string => {
  return ROLE_DISPLAY_NAMES[role.toLowerCase() as RoleType] ?? role;
};
